import * as Popover from '@radix-ui/react-popover';
import type { AgentTemplate } from '@reach/shared-types/agent-templates';
import {
  REQUIRED_SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND,
  SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND,
} from '@reach/shared-types/agents';
import { X } from 'lucide-react';
import { useId } from 'react';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../../app-routes';
import type { GrantableTool } from './agent-templates-api';
import { EDITOR_COPY, systemToolLabel, TEMPLATE_COPY } from './agent-templates-copy';
import { buildAgentTemplateGrantKey, type AgentTemplateGrantTarget } from './grant-key';
import type { GrantedTool } from './template-grants';
import styles from './TemplateToolsEditor.module.css';
import { ToolGrantGroup, type ToolGrantOption } from './ToolGrantGroup';
import { UnavailableGrantRow } from './UnavailableGrantRow';

interface TemplateToolsEditorProps {
  template: AgentTemplate;
  tools: GrantableTool[];
  unavailableGrants: GrantedTool[];
  pendingGrantKey: string | null;
  onAddGrant: (target: AgentTemplateGrantTarget) => void;
  onRemoveGrant: (grantId: string) => void;
}

export const TemplateToolsEditor = ({
  template,
  tools,
  unavailableGrants,
  pendingGrantKey,
  onAddGrant,
  onRemoveGrant,
}: TemplateToolsEditorProps) => {
  const titleId = useId();
  const { editorTitle, editorExplainer } = TEMPLATE_COPY[template.kind];
  const systemToolNames = SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND[template.kind];
  const requiredSystemToolNames: readonly string[] =
    REQUIRED_SYSTEM_TOOL_NAMES_BY_TEMPLATE_KIND[template.kind];
  const mutationInFlight = pendingGrantKey !== null;

  const builtInOptions: ToolGrantOption[] = systemToolNames.map(systemToolName => {
    const grant = template.grants.find(candidate => candidate.systemToolName === systemToolName);
    const grantKey = buildAgentTemplateGrantKey(template.id, { systemToolName });
    const grantIsRequired = requiredSystemToolNames.includes(systemToolName);
    return {
      key: systemToolName,
      label: systemToolLabel(systemToolName),
      checked: grant !== undefined,
      locked: grant !== undefined && grantIsRequired,
      busy: pendingGrantKey === grantKey,
      badge: grantIsRequired ? EDITOR_COPY.requiredBadge : undefined,
      onToggle: () => (grant ? onRemoveGrant(grant.grantId) : onAddGrant({ systemToolName })),
    };
  });

  const partnerToolOptions: ToolGrantOption[] = tools.map(tool => {
    const grant = template.grants.find(candidate => candidate.toolDefinitionId === tool.id);
    const grantKey = buildAgentTemplateGrantKey(template.id, { toolDefinitionId: tool.id });
    return {
      key: tool.id,
      label: tool.display_name,
      description: tool.description || undefined,
      checked: grant !== undefined,
      locked: false,
      busy: pendingGrantKey === grantKey,
      onToggle: () => (grant ? onRemoveGrant(grant.grantId) : onAddGrant({ toolDefinitionId: tool.id })),
    };
  });

  const showBuiltInTools = builtInOptions.length > 0;
  const showUnavailableTools = unavailableGrants.length > 0;
  const requiredHint = requiredSystemToolNames.length > 0 ? EDITOR_COPY.requiredHint : undefined;

  return (
    <Popover.Portal>
      <Popover.Content
        className={styles.editor}
        align="end"
        sideOffset={8}
        collisionPadding={16}
        aria-labelledby={titleId}>
        <header className={styles.header}>
          <h3 id={titleId}>{editorTitle}</h3>
          <Popover.Close className={styles.close} aria-label={EDITOR_COPY.close}>
            <X />
          </Popover.Close>
        </header>
        <p className={styles.explainer}>{editorExplainer}</p>

        {showBuiltInTools && (
          <ToolGrantGroup
            legend={EDITOR_COPY.builtInLegend}
            options={builtInOptions}
            disabled={mutationInFlight}
            hint={requiredHint}
          />
        )}

        <ToolGrantGroup
          legend={EDITOR_COPY.yourToolsLegend}
          options={partnerToolOptions}
          disabled={mutationInFlight}
          emptyNote={
            <p className={styles.noTools}>
              {EDITOR_COPY.noToolsBeforeLink}{' '}
              <Link to={appRoutes.AI_TOOLS}>{EDITOR_COPY.noToolsLinkLabel}</Link>{' '}
              {EDITOR_COPY.noToolsAfterLink}
            </p>
          }
        />

        {showUnavailableTools && (
          <section className={styles.unavailable}>
            <h4>{EDITOR_COPY.unavailableHeading}</h4>
            <p>{EDITOR_COPY.unavailableHint}</p>
            {unavailableGrants.map(granted => (
              <UnavailableGrantRow
                key={granted.grant.grantId}
                label={granted.label}
                disabled={mutationInFlight}
                busy={pendingGrantKey === buildAgentTemplateGrantKey(template.id, granted.grant)}
                onRemove={() => onRemoveGrant(granted.grant.grantId)}
              />
            ))}
          </section>
        )}
      </Popover.Content>
    </Popover.Portal>
  );
};
